<script setup>
import { computed, onActivated, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useFormsStore } from 'dashboard/stores/forms';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { DEAL_AUTOMATION_EVENTS } from '../automation/constants';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import EmbedCodeDialog from 'dashboard/components-next/FormBuilder/EmbedCodeDialog.vue';
import NewCustomFieldDialog from 'dashboard/components-next/FormBuilder/NewCustomFieldDialog.vue';
import {
  FREE_FIELD_TYPES,
  STANDARD_FIELDS,
  buildField,
  buildFreeField,
  customFieldOptions,
  hasIdentifierField,
  isFreeField,
} from 'dashboard/components-next/FormBuilder/fieldCatalog';
import { textColorFor } from '../../../../../forms/color';

const props = defineProps({
  formId: { type: [String, Number], default: null },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();
const formsStore = useFormsStore();
const pipelinesStore = usePipelinesStore();
const agents = useMapGetter('agents/getAgents');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const dealAttributes = useMapGetter('attributes/getDealAttributes');
const automations = useMapGetter('automations/getAutomations');

const embedDialogRef = ref(null);
const newCustomFieldDialogRef = ref(null);
const addFieldKey = ref('');
const freeLabel = ref('');
const freeType = ref('text');

const blank = () => ({
  id: null,
  publicKey: '',
  name: '',
  status: 'inactive',
  pipelineId: null,
  pipelineStageId: null,
  assignedToId: null,
  automationRuleId: null,
  submitLabel: '',
  primaryColor: '#4f46e5',
  successMessage: '',
  redirectUrl: '',
  consentText: '',
  allowedOrigins: '',
  fields: [],
});
const state = reactive(blank());

const isSaving = computed(() => formsStore.uiFlags.saving);
const hasIdentifier = computed(() => hasIdentifierField(state.fields));
const canSave = computed(() => !!state.name.trim() && !isSaving.value);
const isActive = computed({
  get: () => state.status === 'active',
  set: value => {
    state.status = value ? 'active' : 'inactive';
  },
});

// Sem telefone/e-mail o formulário não pode ficar ativo (regra do backend); num formulário novo,
// ativa sozinho assim que o 1º campo desse tipo é adicionado, sem precisar tocar no toggle.
watch(hasIdentifier, (identifierPresent, hadIdentifier) => {
  if (!identifierPresent) {
    state.status = 'inactive';
  } else if (!state.id && hadIdentifier === false) {
    state.status = 'active';
  }
});

const fill = record => {
  const config = record.config || {};
  Object.assign(state, blank(), {
    id: record.id,
    publicKey: record.publicKey,
    name: record.name,
    status: record.status,
    pipelineId: record.pipelineId,
    pipelineStageId: record.pipelineStageId,
    assignedToId: record.assignedToId,
    automationRuleId: record.automationRuleId,
    submitLabel: config.submit_label || '',
    primaryColor: config.primary_color || '#4f46e5',
    successMessage: config.success_message || '',
    redirectUrl: config.redirect_url || '',
    consentText: config.consent_text || '',
    allowedOrigins: (config.allowed_origins || []).join('\n'),
    fields: (config.fields || []).map(field => ({
      ...field,
      options: field.options || [],
      optionsText: (field.options || []).join('\n'),
    })),
  });
};

// --- catálogo de campos -----------------------------------------------------------------------

const groupLabel = group => t(`FORM_BUILDER.EDITOR.GROUPS.${group}`);

const catalog = computed(() => [
  ...STANDARD_FIELDS.map(field => ({
    ...field,
    label: t(`FORM_BUILDER.EDITOR.CATALOG.${field.labelKey}`),
  })),
  ...customFieldOptions(contactAttributes.value, 'contact'),
  ...customFieldOptions(dealAttributes.value, 'deal'),
]);

const NEW_CUSTOM_FIELD_OPTION = '__new_custom_field__';

// O catálogo padrão e o criador de campo personalizado vivem no mesmo menu: a última opção abre
// o miniformulário de criação em vez de adicionar um campo direto.
const addableOptions = computed(() => [
  ...catalog.value
    .filter(option => !state.fields.some(field => field.key === option.key))
    .map(option => ({
      value: option.key,
      label: `${groupLabel(option.group)} · ${option.label}`,
    })),
  {
    value: NEW_CUSTOM_FIELD_OPTION,
    label: t('FORM_BUILDER.EDITOR.FIELDS.NEW_CUSTOM_FIELD'),
  },
]);

const addField = key => {
  addFieldKey.value = '';
  if (key === NEW_CUSTOM_FIELD_OPTION) {
    newCustomFieldDialogRef.value?.open();
    return;
  }
  const option = catalog.value.find(item => item.key === key);
  if (!option) return;
  state.fields.push({
    ...buildField(option, option.label),
    optionsText: (option.options || []).join('\n'),
  });
};

// Campo personalizado inédito (US 05, CA 5.1/5.2): o backend cria o CustomAttributeDefinition
// correspondente ao salvar (Forms::CustomAttributeProvisioningService), sem limite de opções.
const onCustomFieldCreated = field => state.fields.push(field);

const removeField = index => state.fields.splice(index, 1);

const typeOptions = computed(() =>
  FREE_FIELD_TYPES.map(type => ({
    value: type,
    label: t(`FORM_BUILDER.EDITOR.TYPES.${type.toUpperCase()}`),
  }))
);

const addFreeField = () => {
  const label = freeLabel.value.trim();
  if (!label) return;
  state.fields.push({
    ...buildFreeField(
      label,
      freeType.value,
      state.fields.map(field => field.key)
    ),
    optionsText: '',
  });
  freeLabel.value = '';
  freeType.value = 'text';
};

const buttonTextColor = computed(() => textColorFor(state.primaryColor));

const parseOptions = text =>
  text
    .split('\n')
    .map(option => option.trim())
    .filter(Boolean);

const hasOptions = field => ['list', 'radio'].includes(field.type);

// --- destino ----------------------------------------------------------------------------------

const pipelineOptions = computed(() =>
  pipelinesStore.getPipelinesList.map(({ id, name }) => ({
    value: id,
    label: name,
  }))
);

const stageOptions = computed(() => {
  const pipeline = pipelinesStore.getPipelineById(state.pipelineId);
  return (pipeline?.stages || [])
    .filter(stage => stage.stageType === 'regular')
    .map(({ id, name }) => ({ value: id, label: name }));
});

const agentOptions = computed(() => [
  { value: '', label: t('FORM_BUILDER.EDITOR.NO_ASSIGNEE') },
  ...agents.value.map(({ id, name }) => ({ value: id, label: name })),
]);

// Só automações ativas disparadas por evento de negociação fazem sentido aqui: o form sempre
// dispara sobre um Deal (Forms::TriggerAutomationJob), então uma regra de conversa nunca bateria
// as próprias condições/actions.
const automationOptions = computed(() => [
  { value: '', label: t('FORM_BUILDER.EDITOR.NO_AUTOMATION') },
  ...automations.value
    .filter(
      ({ active, event_name: eventName }) =>
        active && DEAL_AUTOMATION_EVENTS.includes(eventName)
    )
    .map(({ id, name }) => ({ value: id, label: name })),
]);

watch(
  () => state.pipelineId,
  (id, previous) => {
    if (previous === undefined || previous === null || id === previous) return;
    state.pipelineStageId = stageOptions.value[0]?.value ?? null;
  }
);

// --- salvar -----------------------------------------------------------------------------------

const payload = computed(() => ({
  id: state.id || undefined,
  name: state.name.trim(),
  status: state.status,
  pipeline_stage_id: state.pipelineStageId || null,
  assigned_to_id: state.assignedToId || null,
  automation_rule_id: state.automationRuleId || null,
  config: {
    submit_label: state.submitLabel.trim(),
    primary_color: state.primaryColor,
    success_message: state.successMessage.trim(),
    redirect_url: state.redirectUrl.trim(),
    consent_text: state.consentText.trim(),
    allowed_origins: parseOptions(state.allowedOrigins),
    fields: state.fields.map(({ optionsText, ...field }) => ({
      ...field,
      options: hasOptions(field) ? parseOptions(optionsText || '') : [],
    })),
  },
}));

const save = async () => {
  try {
    const record = await formsStore.save(payload.value);
    useAlert(t('FORM_BUILDER.EDITOR.SAVED'));
    if (state.id) {
      fill(record);
    } else {
      router.replace(
        accountScopedRoute('forms_settings_edit', { formId: record.id })
      );
    }
  } catch (error) {
    useAlert(
      error.response?.data?.message || t('FORM_BUILDER.EDITOR.SAVE_ERROR')
    );
  }
};

const goBack = () => router.push(accountScopedRoute('forms_settings_index'));

const defaultPipeline = () => {
  if (!state.pipelineId && pipelineOptions.value.length) {
    state.pipelineId = pipelineOptions.value[0].value;
    state.pipelineStageId = stageOptions.value[0]?.value ?? null;
  }
};

// forms_settings_new e forms_settings_edit reaproveitam a mesma instância do componente (mesmo
// component em rotas irmãs): sem isso, sair de uma edição e abrir "Novo formulário" mantinha os
// dados do form anterior em tela.
const load = async () => {
  if (!props.formId) {
    Object.assign(state, blank());
    defaultPipeline();
    return;
  }
  try {
    fill(await formsStore.show(Number(props.formId)));
  } catch {
    useAlert(t('FORM_BUILDER.EDITOR.LOAD_ERROR'));
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('attributes/get');
  store.dispatch('automations/get');
  await pipelinesStore.get();
  await load();
});

watch(() => props.formId, load);

// SettingsWrapper cacheia a tela em <keep-alive> por route.fullPath: "novo formulário" é sempre o
// mesmo caminho, então sair e voltar reaproveita a instância sem passar de novo por onMounted. Sem
// isso, o form recém-criado ficava em tela ao reabrir "Novo formulário".
onActivated(load);
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      :title="
        t(
          state.id
            ? 'FORM_BUILDER.EDITOR.TITLE_EDIT'
            : 'FORM_BUILDER.EDITOR.TITLE_NEW'
        )
      "
      :description="t('FORM_BUILDER.DESCRIPTION')"
    >
      <template #actions>
        <Button
          slate
          faded
          icon="i-lucide-arrow-left"
          :label="t('FORM_BUILDER.BACK')"
          @click="goBack"
        />
        <Button
          v-if="state.publicKey"
          slate
          faded
          icon="i-lucide-code"
          :label="t('FORM_BUILDER.EDITOR.SHOW_CODE')"
          @click="embedDialogRef?.open(state.publicKey)"
        />
      </template>
    </BaseSettingsHeader>

    <div class="grid gap-6 mt-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div class="flex flex-col gap-8 min-w-0">
        <section class="flex flex-col gap-3">
          <h3 class="m-0 font-medium text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.SECTIONS.BASICS') }}
          </h3>
          <Input
            v-model="state.name"
            :label="t('FORM_BUILDER.EDITOR.NAME')"
            :placeholder="t('FORM_BUILDER.EDITOR.NAME_PLACEHOLDER')"
          />
          <div class="flex items-start gap-2">
            <Switch
              v-model="isActive"
              class="mt-0.5"
              :class="{ 'opacity-40 pointer-events-none': !hasIdentifier }"
            />
            <span class="flex flex-col text-sm text-n-slate-12">
              {{ t('FORM_BUILDER.EDITOR.ACTIVE') }}
              <span class="text-xs text-n-slate-11">
                {{
                  hasIdentifier
                    ? t('FORM_BUILDER.EDITOR.ACTIVE_HELP')
                    : t('FORM_BUILDER.EDITOR.FIELDS.NONE')
                }}
              </span>
            </span>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="m-0 font-medium text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.SECTIONS.FIELDS') }}
          </h3>
          <p v-if="!hasIdentifier" class="m-0 text-sm text-n-amber-11">
            {{ t('FORM_BUILDER.EDITOR.FIELDS.NONE') }}
          </p>

          <Draggable
            v-model="state.fields"
            item-key="key"
            handle=".drag-handle"
            class="flex flex-col gap-2"
          >
            <template #item="{ element: field, index }">
              <div
                class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
              >
                <div class="flex items-center gap-2">
                  <Icon
                    icon="i-lucide-grip-vertical"
                    class="cursor-grab drag-handle size-4 text-n-slate-10"
                    :title="t('FORM_BUILDER.EDITOR.FIELDS.DRAG')"
                  />
                  <select
                    v-if="isFreeField(field)"
                    v-model="field.type"
                    class="h-7 px-2 text-xs rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
                    :aria-label="t('FORM_BUILDER.EDITOR.FIELDS.TYPE')"
                  >
                    <option
                      v-for="option in typeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <span v-else class="text-xs text-n-slate-10">
                    {{
                      t(
                        `FORM_BUILDER.EDITOR.TYPES.${(field.type || 'text').toUpperCase()}`
                      )
                    }}
                  </span>
                  <Button
                    class="ltr:ml-auto rtl:mr-auto"
                    sm
                    ghost
                    ruby
                    icon="i-lucide-x"
                    :title="t('FORM_BUILDER.EDITOR.FIELDS.REMOVE')"
                    @click="removeField(index)"
                  />
                </div>
                <div class="grid gap-2 sm:grid-cols-2">
                  <Input
                    v-model="field.label"
                    :label="t('FORM_BUILDER.EDITOR.FIELDS.LABEL')"
                  />
                  <Input
                    v-model="field.placeholder"
                    :label="t('FORM_BUILDER.EDITOR.FIELDS.PLACEHOLDER')"
                  />
                </div>
                <label class="flex items-center gap-2 text-sm text-n-slate-12">
                  <input v-model="field.required" type="checkbox" />
                  {{ t('FORM_BUILDER.EDITOR.FIELDS.REQUIRED') }}
                </label>
                <label
                  v-if="hasOptions(field)"
                  class="flex flex-col gap-1 text-sm text-n-slate-12"
                >
                  {{ t('FORM_BUILDER.EDITOR.FIELDS.OPTIONS') }}
                  <textarea
                    v-model="field.optionsText"
                    rows="3"
                    class="p-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
                  />
                </label>
              </div>
            </template>
          </Draggable>

          <ComboBox
            v-model="addFieldKey"
            :options="addableOptions"
            :placeholder="t('FORM_BUILDER.EDITOR.FIELDS.ADD')"
            :empty-state="t('FORM_BUILDER.EDITOR.FIELDS.ALL_ADDED')"
            @update:model-value="addField"
          />

          <div
            class="flex flex-col gap-2 p-3 border border-dashed rounded-lg border-n-weak"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FORM_BUILDER.EDITOR.FREE.TITLE') }}
            </span>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('FORM_BUILDER.EDITOR.FREE.HINT') }}
            </p>
            <div class="flex flex-wrap items-center gap-2">
              <Input
                v-model="freeLabel"
                class="flex-1 min-w-40"
                :placeholder="t('FORM_BUILDER.EDITOR.FREE.LABEL_PLACEHOLDER')"
                @keydown.enter.prevent="addFreeField"
              />
              <select
                v-model="freeType"
                class="h-10 px-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
                :aria-label="t('FORM_BUILDER.EDITOR.FIELDS.TYPE')"
              >
                <option
                  v-for="option in typeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <Button
                icon="i-lucide-plus"
                :label="t('FORM_BUILDER.EDITOR.FREE.ADD')"
                :disabled="!freeLabel.trim()"
                @click="addFreeField"
              />
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="m-0 font-medium text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.SECTIONS.DESTINATION') }}
          </h3>
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('FORM_BUILDER.EDITOR.DESTINATION_HELP') }}
          </p>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('FORM_BUILDER.EDITOR.PIPELINE') }}
              <ComboBox v-model="state.pipelineId" :options="pipelineOptions" />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('FORM_BUILDER.EDITOR.STAGE') }}
              <ComboBox
                v-model="state.pipelineStageId"
                :options="stageOptions"
              />
            </label>
          </div>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.ASSIGNEE') }}
            <ComboBox
              :model-value="state.assignedToId || ''"
              :options="agentOptions"
              @update:model-value="state.assignedToId = $event || null"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.AUTOMATION') }}
            <ComboBox
              :model-value="state.automationRuleId || ''"
              :options="automationOptions"
              @update:model-value="state.automationRuleId = $event || null"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('FORM_BUILDER.EDITOR.AUTOMATION_HELP') }}
            </span>
          </label>
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="m-0 font-medium text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.SECTIONS.APPEARANCE') }}
          </h3>
          <div class="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Input
              v-model="state.submitLabel"
              :label="t('FORM_BUILDER.EDITOR.SUBMIT_LABEL')"
              :placeholder="t('FORM_BUILDER.EDITOR.SUBMIT_DEFAULT')"
            />
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{ t('FORM_BUILDER.EDITOR.COLOR') }}
              <input
                v-model="state.primaryColor"
                type="color"
                class="h-10 p-1 rounded-lg w-20 bg-n-alpha-black2"
              />
            </label>
          </div>
          <Input
            v-model="state.successMessage"
            :label="t('FORM_BUILDER.EDITOR.SUCCESS_MESSAGE')"
            :placeholder="t('FORM_BUILDER.EDITOR.SUCCESS_DEFAULT')"
          />
          <Input
            v-model="state.redirectUrl"
            :label="t('FORM_BUILDER.EDITOR.REDIRECT_URL')"
            :message="t('FORM_BUILDER.EDITOR.REDIRECT_HELP')"
            placeholder="https://"
          />
          <Input
            v-model="state.consentText"
            :label="t('FORM_BUILDER.EDITOR.CONSENT')"
            :message="t('FORM_BUILDER.EDITOR.CONSENT_HELP')"
          />
        </section>

        <section class="flex flex-col gap-3">
          <h3 class="m-0 font-medium text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.SECTIONS.SECURITY') }}
          </h3>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('FORM_BUILDER.EDITOR.ALLOWED_ORIGINS') }}
            <textarea
              v-model="state.allowedOrigins"
              rows="3"
              placeholder="lp.suaempresa.com.br"
              class="p-2 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('FORM_BUILDER.EDITOR.ALLOWED_HELP') }}
            </span>
          </label>
        </section>

        <Button
          class="self-start"
          :label="t('FORM_BUILDER.EDITOR.SAVE')"
          :is-loading="isSaving"
          :disabled="!canSave"
          @click="save"
        />
      </div>

      <aside
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak h-fit"
      >
        <h3 class="m-0 font-medium text-n-slate-12">
          {{ t('FORM_BUILDER.EDITOR.PREVIEW') }}
        </h3>
        <div
          v-for="field in state.fields"
          :key="field.key"
          class="flex flex-col gap-1"
        >
          <span class="text-xs font-medium text-n-slate-12">
            {{ field.label }}{{ field.required ? ' *' : '' }}
          </span>
          <label
            v-if="field.type === 'checkbox'"
            class="flex items-center gap-2 text-xs text-n-slate-11"
          >
            <input type="checkbox" disabled />
            {{ field.placeholder }}
          </label>
          <select
            v-else-if="field.type === 'list'"
            disabled
            class="h-8 px-2 text-xs rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2"
          >
            <option>{{ field.placeholder }}</option>
          </select>
          <div v-else-if="field.type === 'radio'" class="flex flex-col gap-1">
            <label
              v-for="option in field.options"
              :key="option"
              class="flex items-center gap-2 text-xs text-n-slate-11"
            >
              <input type="radio" disabled />
              {{ option }}
            </label>
          </div>
          <textarea
            v-else-if="field.key === 'message' || field.type === 'textarea'"
            disabled
            rows="2"
            :placeholder="field.placeholder"
            class="px-2 py-1 text-xs rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2"
          />
          <input
            v-else
            disabled
            :type="field.type === 'phone' ? 'tel' : field.type"
            :placeholder="field.placeholder"
            class="h-8 px-2 text-xs rounded-md outline outline-1 outline-n-weak bg-n-alpha-black2"
          />
        </div>
        <label
          v-if="state.consentText"
          class="flex items-start gap-2 text-xs text-n-slate-11"
        >
          <input type="checkbox" disabled class="mt-0.5" />
          {{ state.consentText }}
        </label>
        <!-- SVG para pintar o botão com a cor escolhida sem estilo inline -->
        <svg
          viewBox="0 0 200 40"
          class="w-full h-10 mt-1"
          role="img"
          :aria-label="
            state.submitLabel || t('FORM_BUILDER.EDITOR.SUBMIT_DEFAULT')
          "
        >
          <rect width="200" height="40" rx="6" :fill="state.primaryColor" />
          <text
            x="100"
            y="25"
            text-anchor="middle"
            font-size="14"
            font-weight="600"
            :fill="buttonTextColor"
          >
            {{ state.submitLabel || t('FORM_BUILDER.EDITOR.SUBMIT_DEFAULT') }}
          </text>
        </svg>
      </aside>
    </div>

    <EmbedCodeDialog ref="embedDialogRef" />
    <NewCustomFieldDialog
      ref="newCustomFieldDialogRef"
      @created="onCustomFieldCreated"
    />
  </SettingsLayout>
</template>
